"use client";

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeftRight, ArrowUpDown, CheckCircle2, Coins } from "lucide-react";
import { RangedSelectable, SelectableTypeSafe } from "@erp/shared-libs/common";
import { PreviewObject, useSharedOptions } from "@erp/shared-libs/hooks";
import { formatTenantDate } from "@erp/shared-libs/utils";
import {
  TenantPointChangeType,
  TenantPointLogStatus,
  TenantPointType,
} from "@erp/shared-libs/variables";
import { OptionMenu } from "../menus/option-menu";
import { SearchPreviewBar } from "../search-forms/search-preview-bar";
import { DateRangePicker } from "../ui/date-range-picker";
import { NumberRangePicker } from "../ui/number-range-picker";

// ===================================================
// Types
// ===================================================
/**
 * 點數紀錄列表的篩選欄位（商家端與管理端的紀錄查詢都有這組參數）
 */
export type PointLogFilterFields = {
  /** 點數種類（不帶＝全部） */
  tenantPointTypeId?: TenantPointType;
  /** 異動類型（多選） */
  tenantPointChangeTypeIds?: TenantPointChangeType[];
  /** 結果（成功／失敗，畫面上單選） */
  tenantPointLogStatusIds?: TenantPointLogStatus[];
  /** 建立時間起（含） */
  createdAtStart?: string;
  /** 建立時間訖（含） */
  createdAtEnd?: string;
  /** 異動區間下限（含，可為負數；不帶＝無下限） */
  quantityStart?: number;
  /** 異動區間上限（含，可為負數；不帶＝無上限） */
  quantityEnd?: number;
};

// ===================================================
// Hooks
// ===================================================
/**
 * 點數紀錄篩選的 usePersistParams 欄位定義：七個 preview key 全部註冊（含區間兩端），欄位名稱走 i18n
 */
export const usePointLogSearchOptions = <T extends PointLogFilterFields>() => {
  const { t } = useTranslation("common");

  return useMemo<SelectableTypeSafe<T>[]>(
    () =>
      [
        { label: t("point_log_filter.created_at"), id: "createdAtStart", isSelectable: false },
        { label: t("point_log_filter.created_at"), id: "createdAtEnd", isSelectable: false },
        { label: t("point_log_filter.point_type"), id: "tenantPointTypeId", isSelectable: false },
        {
          label: t("point_log_filter.change_type"),
          id: "tenantPointChangeTypeIds",
          isSelectable: false,
        },
        {
          label: t("point_log_filter.status"),
          id: "tenantPointLogStatusIds",
          isSelectable: false,
        },
        { label: t("point_log_filter.quantity"), id: "quantityStart", isSelectable: false },
        { label: t("point_log_filter.quantity"), id: "quantityEnd", isSelectable: false },
      ] as SelectableTypeSafe<T>[],
    [t]
  );
};

// ===================================================
// Components
// ===================================================
interface PointLogFilterBarProps<T extends PointLogFilterFields> {
  /**
   * usePersistParams 的 preview
   */
  preview: PreviewObject<T>;
  /**
   * 租戶時區（日期篩選與 chip 顯示用）
   */
  timezone?: string;
  /**
   * usePersistParams 的 setPreview
   */
  onPreviewChange: (preview: PreviewObject<T>) => void;
  /**
   * usePersistParams 的 setPreviewByKey
   */
  onPreviewByKey: (obj: Partial<{ [key in keyof T]: T[key] }>) => void;
  /**
   * useTableFeatures 的 handleSearch（套用篩選並回到第 1 頁）
   */
  onSearch: (value: { [key in keyof T]: T[key] }) => void;
  /**
   * useTableFeatures 的 handleClearFilter
   */
  onClearAll: () => void;
}

/**
 * 點數紀錄篩選列：時間區間、點數類型、異動類型（多選）、結果、異動區間（可負數、可只填一邊），
 * 下方預覽列顯示已套用條件並按「查詢」套用；商家端點數紀錄 dialog 與管理端點數管理頁共用
 */
export function PointLogFilterBar<T extends PointLogFilterFields>({
  preview,
  timezone,
  onPreviewChange,
  onPreviewByKey,
  onSearch,
  onClearAll,
}: PointLogFilterBarProps<T>) {
  // ===================================================
  // Hooks
  // ===================================================
  const { t } = useTranslation("common");

  /**
   * 目前選用的日期篩選類型（只有「時間」一種，仍需維持 DateRangePicker 所需 state）
   */
  const [dateSearchOptionId, setDateSearchOptionId] = useState("createdAtRanges");

  /**
   * 點數類型篩選選項
   */
  const pointTypeOptions = useSharedOptions(TenantPointType, "tenant_point_type", [
    TenantPointType.None,
  ]);

  /**
   * 異動類型篩選選項
   */
  const changeTypeOptions = useSharedOptions(TenantPointChangeType, "tenant_point_change_type", [
    TenantPointChangeType.None,
  ]);

  /**
   * 結果篩選選項（成功／失敗）
   */
  const statusOptions = useSharedOptions(TenantPointLogStatus, "tenant_point_log_status", [
    TenantPointLogStatus.None,
  ]);

  // ===================================================
  // Variables
  // ===================================================
  /**
   * 以固定欄位型別存取的 preview（T 至少包含這組欄位）
   */
  const fieldPreview = preview as unknown as PreviewObject<PointLogFilterFields>;

  /**
   * 日期篩選的區間定義
   */
  const dateSearchOptions = useMemo<RangedSelectable<PointLogFilterFields>[]>(
    () => [
      {
        label: t("point_log_filter.created_at"),
        id: "createdAtRanges",
        isSelectable: true,
        fromId: "createdAtStart",
        toId: "createdAtEnd",
      },
    ],
    [t]
  );

  // ===================================================
  // Functions
  // ===================================================
  /**
   * 以固定欄位型別寫入 preview（T 至少包含這組欄位）
   */
  const setFields = (obj: Partial<PointLogFilterFields>) =>
    onPreviewByKey(obj as Partial<{ [key in keyof T]: T[key] }>);

  /**
   * 預覽 chip：單選值「欄位：選項」
   */
  const formatSingle = (name: string, label?: string) =>
    label ? t("point_log_filter.chip_value", { name, value: label }) : undefined;

  /**
   * 預覽 chip：多選值「欄位：選項, 選項（共 N 筆）」
   */
  const formatMultiple = (name: string, labels: string[]) =>
    labels.length
      ? t("point_log_filter.chip_values", {
          name,
          values: labels.join(", "),
          count: labels.length,
        })
      : undefined;

  // ===================================================
  // Render
  // ===================================================
  return (
    <div className="@container flex flex-col gap-3">
      {/* 篩選列 */}
      <div className="flex flex-wrap items-center gap-2">
        {/* 時間區間 */}
        <DateRangePicker
          searchOptions={dateSearchOptions}
          searchOptionId={dateSearchOptionId}
          timezone={timezone}
          onSearchOptionIdChange={setDateSearchOptionId}
          date={{
            from: fieldPreview.createdAtStart?.value
              ? new Date(fieldPreview.createdAtStart.value)
              : undefined,
            to: fieldPreview.createdAtEnd?.value
              ? new Date(fieldPreview.createdAtEnd.value)
              : undefined,
          }}
          onDateChange={setFields}
        />

        {/* 點數類型 */}
        <OptionMenu
          options={pointTypeOptions}
          id={fieldPreview.tenantPointTypeId?.value}
          onChange={(id) => setFields({ tenantPointTypeId: id as TenantPointType })}
        >
          <Coins />
          <p className="@2xl:block ml-3 hidden text-nowrap">
            {fieldPreview.tenantPointTypeId?.columnName}
          </p>
        </OptionMenu>

        {/* 異動類型（多選） */}
        <OptionMenu
          mutiple
          options={changeTypeOptions}
          ids={fieldPreview.tenantPointChangeTypeIds?.value ?? []}
          onChange={(ids) =>
            setFields({ tenantPointChangeTypeIds: ids as TenantPointChangeType[] })
          }
        >
          <ArrowLeftRight />
          <p className="@2xl:block ml-3 hidden text-nowrap">
            {fieldPreview.tenantPointChangeTypeIds?.columnName}
          </p>
        </OptionMenu>

        {/* 結果（成功／失敗，單選；送出時包成一個元素的陣列） */}
        <OptionMenu
          options={statusOptions}
          id={fieldPreview.tenantPointLogStatusIds?.value?.[0]}
          onChange={(id) => setFields({ tenantPointLogStatusIds: [id as TenantPointLogStatus] })}
        >
          <CheckCircle2 />
          <p className="@2xl:block ml-3 hidden text-nowrap">
            {fieldPreview.tenantPointLogStatusIds?.columnName}
          </p>
        </OptionMenu>

        {/* 異動區間：可為負數，空欄代表無界（最小空＝無下限、最大空＝無上限） */}
        <NumberRangePicker<PointLogFilterFields>
          range={{ fromId: "quantityStart", toId: "quantityEnd" }}
          from={fieldPreview.quantityStart?.value}
          to={fieldPreview.quantityEnd?.value}
          onRangeChange={setFields}
        >
          <ArrowUpDown />
          <p className="@2xl:block ml-3 hidden text-nowrap">
            {fieldPreview.quantityStart?.columnName}
          </p>
        </NumberRangePicker>
      </div>

      {/* 已套用條件 */}
      <SearchPreviewBar<PointLogFilterFields>
        hideColumns={["createdAtEnd", "quantityEnd"]}
        rangeColumns={["quantityStart"]}
        previewValue={fieldPreview}
        onPreviewValueChange={(value) => onPreviewChange(value as unknown as PreviewObject<T>)}
        onClearAll={onClearAll}
        onSearch={(value) => onSearch(value as unknown as { [key in keyof T]: T[key] })}
        onClear={{
          createdAtStart() {
            setFields({ createdAtStart: undefined, createdAtEnd: undefined });
          },
          quantityStart() {
            setFields({ quantityStart: undefined, quantityEnd: undefined });
          },
        }}
        formatValue={{
          tenantPointTypeId: ({ columnName, value }) =>
            formatSingle(columnName, pointTypeOptions.find((o) => o.value === value)?.label),
          tenantPointChangeTypeIds: ({ columnName, value }) =>
            formatMultiple(
              columnName,
              ((value as TenantPointChangeType[] | undefined) ?? [])
                .map((id) => changeTypeOptions.find((o) => o.value === id)?.label ?? "")
                .filter(Boolean)
            ),
          tenantPointLogStatusIds: ({ columnName, value }) =>
            formatSingle(
              columnName,
              statusOptions.find(
                (o) => o.value === (value as TenantPointLogStatus[] | undefined)?.[0]
              )?.label
            ),
          createdAtStart: ({ columnName, origin }) =>
            t("point_log_filter.chip_range", {
              name: columnName,
              from: origin.createdAtStart?.value
                ? formatTenantDate(origin.createdAtStart.value, timezone)
                : "-",
              to: origin.createdAtEnd?.value
                ? formatTenantDate(origin.createdAtEnd.value, timezone)
                : "-",
            }),
          quantityStart: ({ columnName, origin }) => {
            const min = origin.quantityStart?.value;
            const max = origin.quantityEnd?.value;
            // 兩端皆未填 → 不顯示 chip
            if (min == null && max == null) return undefined;

            return t("point_log_filter.chip_range", {
              name: columnName,
              from: min != null ? min.toLocaleString() : "-",
              to: max != null ? max.toLocaleString() : "-",
            });
          },
        }}
      />
    </div>
  );
}
